import { useEffect, useRef, useState } from 'react';

const categories = [
  {
    label: 'Colegios',
    icon: 'ri-building-line',
    clients: [
      {
        name: 'I.E.P. Peruano Español',
        logo: 'https://public.readdy.ai/ai/img_res/a3a3b3b9-8526-496a-93d1-7247f3e5a881.png',
      },
      {
        name: 'I.E. CIMA',
        logo: 'https://public.readdy.ai/ai/img_res/730e301a-990d-4173-b537-13cae4763e9b.png',
      },
      {
        name: 'C.P. Manuel Antonio Rivas',
        logo: 'https://static.readdy.ai/image/637245dcd209b8280a76b9094dfb4864/77cfb3c480c13661a87d9a2612a1cb26.jpeg',
      },
      {
        name: 'I.E. Amancio Varona - Tumán',
        logo: 'https://static.readdy.ai/image/637245dcd209b8280a76b9094dfb4864/3100eaa57e1faf388fce279a10a6a397.jpeg',
      },
      {
        name: 'I.E. Salazar Bondy',
        logo: 'https://public.readdy.ai/ai/img_res/8afd7661-4b43-42fb-b7f8-9e8a5af9eb7b.png',
      },
      {
        name: 'I.E. Johans Kepler - Tumán',
        logo: 'https://public.readdy.ai/ai/img_res/743e1f39-710f-4bcc-b157-de62a292d44a.png',
      },
    ],
  },
  {
    label: 'Academias Preuniversitarias',
    icon: 'ri-graduation-cap-line',
    clients: [
      {
        name: 'Élite Academia Preuniversitaria',
        logo: 'https://static.readdy.ai/image/637245dcd209b8280a76b9094dfb4864/ff113a883c95e9be89d23f6172af682a.jpeg',
      },
      {
        name: 'Ágora Academia Preuniversitaria',
        logo: 'https://static.readdy.ai/image/637245dcd209b8280a76b9094dfb4864/db2cb7868ed9b1464f6f4f93898d4c96.jpeg',
      },
    ],
  },
  {
    label: 'Entidades',
    icon: 'ri-government-line',
    clients: [
      {
        name: 'SUNARP',
        logo: 'https://static.readdy.ai/image/637245dcd209b8280a76b9094dfb4864/c1c9dc4f5f5b05360aa108dbdc148887.png',
      },
      {
        name: 'Gerencia Regional de Salud',
        logo: 'https://static.readdy.ai/image/637245dcd209b8280a76b9094dfb4864/fe04401b9ecd23145f4f38776a5ab6f5.png',
      },
      {
        name: 'Universidad Intercultural "Fabiola Salazar Leguía" de Bagua',
        logo: 'https://static.readdy.ai/image/637245dcd209b8280a76b9094dfb4864/9a94f1f258169ce496ddcf8ab0ea2c85.png',
      },
    ],
  },
];

function useCountUp(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

export default function Clients() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const count1 = useCountUp(100, 1500, visible);
  const count2 = useCountUp(50000, 1800, visible);
  const count3 = useCountUp(100, 1400, visible);

  return (
    <section id="clientes" ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#0D9488]/10 px-6 py-3 rounded-full mb-6">
            <i className="ri-handshake-line text-[#0D9488] text-xl"></i>
            <span className="text-[#0D9488] font-semibold text-sm">Nuestros Clientes</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A2E] mb-4">
            Instituciones que{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0D9488] to-[#F59E0B]">
              confían en nosotros
            </span>
          </h2>
          <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Trabajamos con colegios, academias preuniversitarias y entidades públicas que apuestan por la precisión y la tecnología en sus evaluaciones.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {[
            { value: count1, suffix: '+', label: 'Instituciones aliadas', border: 'border-[#0D9488]' },
            { value: count2, suffix: '+', label: 'Evaluaciones procesadas', border: 'border-[#F59E0B]' },
            { value: count3, suffix: '%', label: 'Satisfacción del cliente', border: 'border-[#0D9488]' },
          ].map((stat, i) => (
            <div
              key={i}
              className={`bg-[#F8F9FA] rounded-2xl p-6 md:p-8 text-center border-t-4 ${stat.border} shadow-sm hover:shadow-md transition-shadow`}
            >
              <p className="text-4xl md:text-5xl font-bold text-[#1A1A2E] mb-2">
                {stat.value.toLocaleString()}{stat.suffix}
              </p>
              <p className="text-gray-500 font-medium text-xs md:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Categories */}
        <div className="space-y-14">
          {categories.map((cat) => (
            <div key={cat.label}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0D9488]/10 rounded-xl">
                  <i className={`${cat.icon} text-[#0D9488] text-xl`}></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-[#1A1A2E]">{cat.label}</h3>
                <div className="flex-1 h-px bg-gray-100 ml-2"></div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {cat.clients.map((client) => (
                  <div
                    key={client.name}
                    className="group bg-white border border-gray-100 rounded-2xl p-4 md:p-6 flex flex-col items-center justify-center gap-3 md:gap-4 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-default"
                  >
                    <div className="w-full h-16 md:h-20 flex items-center justify-center">
                      <img
                        src={client.logo}
                        alt={client.name}
                        className="max-h-16 md:max-h-20 max-w-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500"
                      />
                    </div>
                    <p className="text-xs font-semibold text-gray-400 group-hover:text-[#0D9488] text-center transition-colors duration-300 leading-tight">
                      {client.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 bg-gradient-to-r from-[#0D9488] to-[#0F766E] rounded-3xl p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4"></div>
          <div className="absolute left-1/2 bottom-0 w-40 h-40 bg-[#F59E0B]/10 rounded-full translate-y-1/2"></div>
          <div className="relative text-center md:text-left">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-2">
              ¿Tu institución aún no trabaja con nosotros?
            </h3>
            <p className="text-white/80 text-sm md:text-base">
              Únete a las instituciones que ya optimizaron sus evaluaciones con tecnología OMR.
            </p>
          </div>
          <a
            href="#contacto"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="relative bg-white text-[#0D9488] font-bold px-6 md:px-8 py-3 md:py-4 rounded-xl hover:bg-[#F59E0B] hover:text-white transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer text-sm"
          >
            Contáctanos ahora
          </a>
        </div>

      </div>
    </section>
  );
}